import type { SearchParamsValue } from '@shared/components/SearchParamsObserver/SearchParamsObserver';
import { calculateStayDates, createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { isBefore, isToday, isFuture, format } from 'date-fns';
import { DEFAULT_OCCUPANCY } from './constants';

//================================================
// Derive search date query params
//================================================

const toDateObject = (date: { year: number; month: number; day: number }) => {
  return new Date(date.year, date.month - 1, date.day);
};

type DeriveSearchDateQueryParamsArgs = {
  dateSearchParams: Pick<SearchParamsValue, 'startDate' | 'endDate'>;
  eventFinalizedDateString: Maybe<string>;
  settingsCheckInDateString?: Maybe<string>;
  settingsCheckOutDateString?: Maybe<string>;
  useDefaults?: boolean;
};

export const deriveSearchDateQueryParams = ({
  useDefaults,
  dateSearchParams,
  eventFinalizedDateString,
  settingsCheckInDateString,
  settingsCheckOutDateString,
}: DeriveSearchDateQueryParamsArgs) => {
  // Date search params are valid (i.e. not null or undefined, and are dates)
  if (!useDefaults && dateSearchParams.endDate.valid && dateSearchParams.startDate.valid) {
    const checkinDate = toDateObject(dateSearchParams.startDate.value);
    const checkoutDate = toDateObject(dateSearchParams.endDate.value);

    // Confirm that:
    // 1. Start date is either today or in the future
    // 2. End date is after start date
    if ((isToday(checkinDate) || isFuture(checkinDate)) && isBefore(checkinDate, checkoutDate)) {
      return {
        checkin: format(checkinDate, 'yyyy-MM-dd'),
        checkout: format(checkoutDate, 'yyyy-MM-dd'),
        isMissingInUrl: false,
      };
    }
  }

  // If query params are missing and settings has an explicit range, prioritize that.
  if (useDefaults && settingsCheckInDateString && settingsCheckOutDateString) {
    const settingsCheckInDate = createDateFromUnformattedString(settingsCheckInDateString);
    const settingsCheckOutDate = createDateFromUnformattedString(settingsCheckOutDateString);

    if (settingsCheckInDate && settingsCheckOutDate && (isToday(settingsCheckInDate) || isFuture(settingsCheckInDate)) && isBefore(settingsCheckInDate, settingsCheckOutDate)) {
      return {
        isMissingInUrl: true,
        checkin: format(settingsCheckInDate, 'yyyy-MM-dd'),
        checkout: format(settingsCheckOutDate, 'yyyy-MM-dd'),
      };
    }
  }

  // Fallback strategy when date search params are not valid:
  // 1. Use event finalized date
  // 2. If no event date, choose dates 1 month from today
  const { checkIn: eventStartDate, checkOut: eventEndDate } = calculateStayDates(eventFinalizedDateString);

  return {
    checkin: eventStartDate.dateString,
    checkout: eventEndDate.dateString,
    isMissingInUrl: true,
  };
};

//================================================
// Derive rooms query params
//================================================

type UseDeriveRoomsQueryParamsArgs = {
  roomsSearchParams: Pick<SearchParamsValue, 'rooms'>;
  useDefaults: boolean;
};

export const deriveRoomsQueryParams = ({ roomsSearchParams, useDefaults }: UseDeriveRoomsQueryParamsArgs) => {
  if (!useDefaults && roomsSearchParams.rooms.valid) {
    return {
      rooms: roomsSearchParams.rooms.value,
      isMissingInUrl: false,
    };
  }

  return {
    rooms: DEFAULT_OCCUPANCY.occupancies,
    isMissingInUrl: true,
  };
};
